import { useState, useCallback, useEffect, useRef } from 'react';
import type { DataProvider } from 'react-admin';

export interface WorkflowTransition {
  sequence: number;
  event: string;
  fromState: string | null;
  toState: string;
  timestamp: string;
  triggeredBy: string;
}

export interface WorkflowInstance {
  id: string;
  workflowType: string;
  entityId: string;
  entityName: string;
  currentState: {
    value: string;
    context: Record<string, unknown>;
  };
  status: 'active' | 'paused' | 'completed' | 'cancelled';
  workflowVersion: string;
  startedAt: string;
  lastActionAt: string;
  pausedAt: string | null;
  breakpointId: string | null;
  relatedEntityId: string;
  transitions: WorkflowTransition[];
}

interface UseWorkflowListOptions {
  dataProvider?: DataProvider;
  resource?: string;
  filters?: {
    status?: string[];
    workflowType?: string[];
    entityType?: string[];
    search?: string;
  };
  page?: number;
  perPage?: number;
}

interface UseWorkflowListReturn {
  workflows: WorkflowInstance[];
  total: number;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    perPage: number;
    totalPages: number;
  };
  refetch: () => void;
}

export function useWorkflowList({
  dataProvider,
  resource = 'workflow-instances',
  filters = {},
  page = 1,
  perPage = 10,
}: UseWorkflowListOptions = {}): UseWorkflowListReturn {
  const [workflows, setWorkflows] = useState<WorkflowInstance[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refreshTriggerRef = useRef(0);
  useEffect(() => {
    refreshTriggerRef.current = refreshTrigger;
  }, [refreshTrigger]);

  const refetch = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const fetchWorkflows = useCallback(async () => {
    if (!dataProvider) {
      setError('Data provider not available');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await dataProvider.getList(resource, {
        pagination: { page, perPage },
        sort: { field: 'lastActionAt', order: 'DESC' },
        filter: {},
      });

      const allData = response.data as WorkflowInstance[];
      let filtered = allData;
      const currentFilters = filtersRef.current;

      if (currentFilters.status && currentFilters.status.length > 0) {
        filtered = filtered.filter((w: WorkflowInstance) =>
          currentFilters.status!.includes(w.status)
        );
      }

      if (currentFilters.workflowType && currentFilters.workflowType.length > 0) {
        const filterTypes = currentFilters.workflowType.map(t => t.toLowerCase());
        filtered = filtered.filter((w: WorkflowInstance) =>
          filterTypes.includes(w.workflowType.toLowerCase())
        );
      }

      if (currentFilters.search && currentFilters.search.trim()) {
        const searchLower = currentFilters.search.toLowerCase().trim();
        filtered = filtered.filter((w: WorkflowInstance) =>
          w.entityName?.toLowerCase().includes(searchLower) ||
          w.entityId?.toLowerCase().includes(searchLower)
        );
      }

      setWorkflows(filtered);
      setTotal(filtered.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch workflows');
      setWorkflows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [dataProvider, resource, page, perPage]);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  const totalPages = Math.ceil(total / perPage);

  return {
    workflows,
    total,
    isLoading,
    error,
    pagination: {
      page,
      perPage,
      totalPages,
    },
    refetch,
  };
}